import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useI18nContext } from "@procore/core-react";
import { useToastAlertContext } from "@procore/toast-alert";
import { ProcoreStore, type GetProcoreEnvironmentResponse } from "@/stores/procore-store.core";
import { requestContext } from "@/stores/common/request-context";

export type ClassificationOption = { id: string; label: string };

export const DEFAULT_PROCORE_ENVIRONMENT: GetProcoreEnvironmentResponse = {
   company_id: "",
   company_name: "",
   user_id: "",
   locale: "en",
   currency_symbol: "$",
   currency_display: "USD",
   currency_iso_code: "USD",
   time_zone: "America/New_York",
   date_format: "MM/DD/YYYY",
};

export async function getProcoreEnvironment(): Promise<GetProcoreEnvironmentResponse> {
   if (!requestContext.usingProcoreHostApp) {
      return DEFAULT_PROCORE_ENVIRONMENT;
   }
   return await ProcoreStore.getProcoreCurrencyConfiguration().payload;
}

export function useGetProcoreEnvironment() {
   return useQuery({
      queryKey: ["procore-environment"],
      queryFn: getProcoreEnvironment,
      staleTime: Infinity, // Environment data rarely changes during a session
   });
}

export function useWorkClassifications(options?: { enabled?: boolean }) {
   const { showToast } = useToastAlertContext();
   const I18n = useI18nContext();

   const query = useInfiniteQuery({
      queryKey: ["work-classifications"],
      enabled: options?.enabled ?? true,
      queryFn: async ({ pageParam }) => {
         const page = await ProcoreStore.getWorkClassificationsPage(pageParam);
         return {
            options: page.items.map(
               (wc): ClassificationOption => ({ id: String(wc.id), label: wc.name }),
            ),
            nextPage: page.nextPage,
         };
      },
      initialPageParam: 1,
      getNextPageParam: (last) => last.nextPage ?? undefined,
      staleTime: Infinity,
   });

   useEffect(() => {
      if (query.isError) {
         showToast.error(
            I18n.t("views.company.workforce_planning.people.classifications_load_error"),
         );
      }
   }, [query.isError, showToast, I18n]);

   return query;
}

/**
 * Loads every page of work classifications up front and returns an id -> label map. Unlike
 * `useWorkClassifications`, callers don't need their own paging effect: the queryFn loops the
 * pages itself.
 */
export function useGetWorkClassificationLabels(options?: { enabled?: boolean }) {
   const { showToast } = useToastAlertContext();
   const I18n = useI18nContext();

   const query = useQuery({
      queryKey: ["work-classification-labels"],
      enabled: options?.enabled ?? true,
      queryFn: async (): Promise<Record<string, string>> => {
         const labels: Record<string, string> = {};
         let page: number | undefined = 1;
         while (page !== undefined) {
            const result = await ProcoreStore.getWorkClassificationsPage(page);
            for (const wc of result.items) {
               labels[String(wc.id)] = wc.name;
            }
            page = result.nextPage ?? undefined;
         }
         return labels;
      },
      staleTime: Infinity,
   });

   useEffect(() => {
      if (query.isError) {
         showToast.error(
            I18n.t("views.company.workforce_planning.people.classifications_load_error"),
         );
      }
   }, [query.isError, showToast, I18n]);

   return query;
}

export function useWorkClassification(id: string | null | undefined) {
   const { showToast } = useToastAlertContext();
   const I18n = useI18nContext();

   const query = useQuery({
      queryKey: ["work-classification", id],
      queryFn: async (): Promise<ClassificationOption | null> => {
         if (!id) return null;
         const wc = await ProcoreStore.getWorkClassificationById(id);
         return wc ? { id: String(wc.id), label: wc.name } : null;
      },
      enabled: !!id,
      staleTime: Infinity,
   });

   useEffect(() => {
      if (query.isError) {
         showToast.error(
            I18n.t("views.company.workforce_planning.people.classifications_load_error"),
         );
      }
   }, [query.isError, showToast, I18n]);

   return query;
}
